<script setup lang="ts">
import { computed, ref, useAttrs, useSlots, watch } from 'vue'

import { IconField, InputIcon } from 'primevue'
import FloatLabel from 'primevue/floatlabel'
import MultiSelect, { type MultiSelectChangeEvent } from 'primevue/multiselect'

import type { SelectOption } from './types/select-option.types'

type MultiSelectSize = 'small' | 'large'
type MultiSelectDisplay = 'comma' | 'chip'

interface Props {
  modelValue?: (string | number | boolean)[]
  options: SelectOption[]
  label?: string
  placeholder?: string
  showClear?: boolean
  disabled?: boolean
  loading?: boolean
  filter?: boolean
  filterPlaceholder?: string
  filterLocale?: string
  filterMatchMode?: 'startsWith' | 'contains' | 'endsWith'
  filterFields?: string[]
  resetFilterOnHide?: boolean
  display?: MultiSelectDisplay
  selectedItemsLabel?: string
  maxSelectedLabels?: number
  selectionLimit?: number
  showToggleAll?: boolean
  optionLabel?: string
  optionValue?: string
  optionDisabled?: string
  optionGroupLabel?: string
  optionGroupChildren?: string
  size?: MultiSelectSize
  scrollHeight?: string
  autoFilterFocus?: boolean
  selectAll?: boolean
  customClass?: string
  containerClass?: string
  inputClass?: string
  optionClass?: string
  filterInputClass?: string
  emptyMessageClass?: string
  errorClass?: string
  errorMessage?: string
  showErrorMessage?: boolean
  inputId?: string
  pt?: object
  ptOptions?: object
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  label: '',
  placeholder: 'Seleccione...',
  showClear: true,
  disabled: false,
  loading: false,
  filter: false,
  filterPlaceholder: 'Search...',
  filterMatchMode: 'contains',
  resetFilterOnHide: true,
  display: 'comma',
  selectedItemsLabel: '{0} items selected',
  maxSelectedLabels: 3,
  showToggleAll: true,
  optionLabel: 'name',
  optionValue: 'value',
  size: 'small',
  scrollHeight: '200px',
  autoFilterFocus: false,
  selectAll: false,
  customClass: '',
  containerClass: 'w-full min-w-0',
  inputClass: 'w-full !rounded-lg !text-xs py-0.5! text-black-400 !bg-white dark:!bg-transparent shadow-sm! transition-colors duration-200 !border-white/60 dark:!border-[var(--p-primary-color)]/40 hover:!border-white dark:hover:!border-[var(--p-primary-color)]/60 hover:!shadow-md focus:!border-[var(--p-primary-color)]',
  optionClass: '!text-xs 2xl:text-sm!',
  filterInputClass: '!text-xs 2xl:text-sm!',
  emptyMessageClass: '!text-xs',
  errorClass: 'text-red-400 dark:text-red-300 p-0 m-0',
  errorMessage: '',
  showErrorMessage: true,
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: (string | number | boolean)[]]
  change: [event: MultiSelectChangeEvent]
  focus: [event: Event]
  blur: [event: Event]
  'before-show': []
  'before-hide': []
  show: []
  hide: []
  filter: [event: { originalEvent: Event; value: string }]
  'select-all-change': [event: { originalEvent: Event; checked: boolean }]
}>()

const attrs = useAttrs()
const slots = useSlots()

// Generar ID único para el input
const inputId = computed(() => props.inputId || `msel-${crypto.randomUUID()}`)

// Filtrar atributos conflictivos
const blockKeys = [
  'modelValue',
  'onUpdate:modelValue',
  'class',
  'placeholder',
  'optionClass',
  'option-class',
  'filterInputClass',
  'filter-input-class',
  'emptyMessageClass',
  'empty-message-class',
]

const forwardedAttrs = computed(() => {
  const src = attrs ?? {}
  return Object.fromEntries(Object.entries(src).filter(([k]) => !blockKeys.includes(k)))
})

// Ajustes visuales internos del MultiSelect.
const ptMerged = computed(() => ({
  root: {
    class: [slots.icon ? 'pl-9' : '', props.display === 'chip' ? 'app-multiselect--chip' : '']
      .filter(Boolean)
      .join(' '),
  },
  label: { class: 'pl-1 !text-xs' },
  chip: { class: '!text-[10px]' },
  overlay: { class: 'app-multiselect-glass-panel app-card-glass backdrop-blur-sm rounded-lg shadow-xl overflow-hidden' },
  header: { class: 'bg-transparent border-0' },
  listContainer: { class: 'bg-transparent' },
  list: { class: 'bg-transparent' },
  option: { class: optionClasses.value },
  optionLabel: { class: optionClasses.value },
  pcFilter: {
    root: { class: filterInputClasses.value },
  },
  emptyMessage: { class: emptyMessageClasses.value },
  dropdown: { class: 'pr-3' },
  ...((props.pt as object) || {}),
}))

const effectivePt = computed(() => ptMerged.value)

// Manejar clases del contenedor
const containerClasses = computed(() => {
  const classAttr = attrs.class as string | undefined
  return classAttr ? `${props.containerClass} ${classAttr}` : props.containerClass
})

// Placeholder efectivo para FloatLabel
/* const effectivePlaceholder = computed(() => {
  return props.placeholder || props.label || ' '
}) */

// Determinar si hay error
const hasError = computed(() => props.errorMessage.length > 0)

// Clases del input con estado de error
const inputClasses = computed(() => {
  const classes = [props.inputClass, props.customClass]
  if (hasError.value) classes.push('p-invalid')
  return classes.filter(Boolean).join(' ')
})

const optionClasses = computed(() => {
  const classAttr = attrs.optionClass || attrs['option-class']
  return [props.optionClass, classAttr].filter(Boolean).join(' ')
})

const filterInputClasses = computed(() => {
  const classAttr = attrs.filterInputClass || attrs['filter-input-class']
  return [props.filterInputClass, classAttr].filter(Boolean).join(' ')
})

const emptyMessageClasses = computed(() => {
  const classAttr = attrs.emptyMessageClass || attrs['empty-message-class']
  return [props.emptyMessageClass, classAttr].filter(Boolean).join(' ')
})

const selectedValues = ref<(string | number | boolean)[]>([])

const optionValuesAreEqual = (
  left: (string | number | boolean)[] = [],
  right: (string | number | boolean)[] = [],
) => left.length === right.length && left.every((value, index) => Object.is(value, right[index]))

const selectableOptionValues = computed(() =>
  props.options
    .filter((option) => {
      if (!props.optionDisabled) return true
      const disabledKey = props.optionDisabled as keyof SelectOption
      return !option[disabledKey]
    })
    .map((option) => {
      const optionValueKey = props.optionValue as keyof SelectOption
      return option[optionValueKey] as string | number | boolean
    }),
)

const allSelected = computed(() => {
  if (!selectableOptionValues.value.length) return false

  const selectedSet = new Set(selectedValues.value)
  return selectableOptionValues.value.every((value) => selectedSet.has(value))
})

// Sincronizar con modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    const nextValue = newValue || []
    if (optionValuesAreEqual(nextValue, selectedValues.value)) return
    selectedValues.value = nextValue
  },
  { immediate: true },
)

// Emitir cambios
watch(selectedValues, (newValue) => {
  if (optionValuesAreEqual(newValue, props.modelValue || [])) return
  emit('update:modelValue', newValue)
})

// Manejar eventos
const handleChange = (event: MultiSelectChangeEvent) => {
  selectedValues.value = (event.value as (string | number | boolean)[]) || []
  emit('change', event)
}

const handleSelectAllChange = (event: { originalEvent: Event; checked: boolean }) => {
  selectedValues.value = event.checked ? [...selectableOptionValues.value] : []

  emit('select-all-change', event)
}
</script>

<template>
  <div class="app-multiselect-field" :class="containerClasses">
    <!-- Con label y icono -->
    <FloatLabel variant="on" v-if="label && $slots.icon">
      <IconField class="w-full">
        <InputIcon>
          <slot name="icon" />
        </InputIcon>
        <MultiSelect
          v-bind="forwardedAttrs"
          :inputId="inputId"
          v-model="selectedValues"
          :options="options"
          :option-label="optionLabel"
          :option-value="optionValue"
          :option-disabled="optionDisabled"
          :option-group-label="optionGroupLabel"
          :option-group-children="optionGroupChildren"
          :show-clear="showClear"
          :disabled="disabled"
          :loading="loading"
          :filter="filter"
          :filter-placeholder="filterPlaceholder"
          :filter-locale="filterLocale"
          :filter-match-mode="filterMatchMode"
          :filter-fields="filterFields"
          :reset-filter-on-hide="resetFilterOnHide"
          :display="display"
          :selected-items-label="selectedItemsLabel"
          :max-selected-labels="maxSelectedLabels"
          :selection-limit="selectionLimit"
          :show-toggle-all="showToggleAll"
          :size="size"
          :scroll-height="scrollHeight"
          :auto-filter-focus="autoFilterFocus"
          :select-all="selectAll || allSelected"
          :class="inputClasses"
          :pt="effectivePt"
          :pt-options="ptOptions"
          @change="handleChange"
          @focus="emit('focus', $event)"
          @blur="emit('blur', $event)"
          @before-show="emit('before-show')"
          @before-hide="emit('before-hide')"
          @show="emit('show')"
          @hide="emit('hide')"
          @filter="emit('filter', $event)"
          @selectall-change="handleSelectAllChange"
        />
      </IconField>
      <label :for="inputId" class="text-xs 2xl:text-sm">{{ label }}</label>
    </FloatLabel>

    <!-- Con label sin icono -->
    <FloatLabel variant="on" v-else-if="label">
      <MultiSelect
        v-bind="forwardedAttrs"
        :inputId="inputId"
        v-model="selectedValues"
        :options="options"
        :option-label="optionLabel"
        :option-value="optionValue"
        :option-disabled="optionDisabled"
        :option-group-label="optionGroupLabel"
        :option-group-children="optionGroupChildren"
        :show-clear="showClear"
        :disabled="disabled"
        :loading="loading"
        :filter="filter"
        :filter-placeholder="filterPlaceholder"
        :filter-locale="filterLocale"
        :filter-match-mode="filterMatchMode"
        :filter-fields="filterFields"
        :reset-filter-on-hide="resetFilterOnHide"
        :display="display"
        :selected-items-label="selectedItemsLabel"
        :max-selected-labels="maxSelectedLabels"
        :selection-limit="selectionLimit"
        :show-toggle-all="showToggleAll"
        :size="size"
        :scroll-height="scrollHeight"
        :auto-filter-focus="autoFilterFocus"
        :select-all="selectAll || allSelected"
        :class="inputClasses"
        :pt="effectivePt"
        :pt-options="ptOptions"
        @change="handleChange"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @before-show="emit('before-show')"
        @before-hide="emit('before-hide')"
        @show="emit('show')"
        @hide="emit('hide')"
        @filter="emit('filter', $event)"
        @selectall-change="handleSelectAllChange"
      />
      <label :for="inputId" class="text-sm">{{ label }}</label>
    </FloatLabel>

    <!-- Sin label con icono -->
    <IconField v-else-if="$slots.icon" class="w-full">
      <InputIcon>
        <slot name="icon" />
      </InputIcon>
      <MultiSelect
        v-bind="forwardedAttrs"
        :inputId="inputId"
        v-model="selectedValues"
        :options="options"
        :option-label="optionLabel"
        :option-value="optionValue"
        :option-disabled="optionDisabled"
        :option-group-label="optionGroupLabel"
        :option-group-children="optionGroupChildren"
        :placeholder="placeholder"
        :show-clear="showClear"
        :disabled="disabled"
        :loading="loading"
        :filter="filter"
        :filter-placeholder="filterPlaceholder"
        :filter-locale="filterLocale"
        :filter-match-mode="filterMatchMode"
        :filter-fields="filterFields"
        :reset-filter-on-hide="resetFilterOnHide"
        :display="display"
        :selected-items-label="selectedItemsLabel"
        :max-selected-labels="maxSelectedLabels"
        :selection-limit="selectionLimit"
        :show-toggle-all="showToggleAll"
        :size="size"
        :scroll-height="scrollHeight"
        :auto-filter-focus="autoFilterFocus"
        :select-all="selectAll || allSelected"
        :class="inputClasses"
        :pt="effectivePt"
        :pt-options="ptOptions"
        @change="handleChange"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @before-show="emit('before-show')"
        @before-hide="emit('before-hide')"
        @show="emit('show')"
        @hide="emit('hide')"
        @filter="emit('filter', $event)"
        @selectall-change="handleSelectAllChange"
      />
    </IconField>

    <!-- Sin label sin icono -->
    <MultiSelect
      v-else
      v-bind="forwardedAttrs"
      v-model="selectedValues"
      :options="options"
      :option-label="optionLabel"
      :option-value="optionValue"
      :option-disabled="optionDisabled"
      :option-group-label="optionGroupLabel"
      :option-group-children="optionGroupChildren"
      :placeholder="placeholder"
      :show-clear="showClear"
      :disabled="disabled"
      :loading="loading"
      :filter="filter"
      :filter-placeholder="filterPlaceholder"
      :filter-locale="filterLocale"
      :filter-match-mode="filterMatchMode"
      :filter-fields="filterFields"
      :reset-filter-on-hide="resetFilterOnHide"
      :display="display"
      :selected-items-label="selectedItemsLabel"
      :max-selected-labels="maxSelectedLabels"
      :selection-limit="selectionLimit"
      :show-toggle-all="showToggleAll"
      :size="size"
      :scroll-height="scrollHeight"
      :auto-filter-focus="autoFilterFocus"
      :select-all="selectAll || allSelected"
      :class="inputClasses"
      :pt="effectivePt"
      :pt-options="ptOptions"
      @change="handleChange"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
      @before-show="emit('before-show')"
      @before-hide="emit('before-hide')"
      @show="emit('show')"
      @hide="emit('hide')"
      @filter="emit('filter', $event)"
      @selectall-change="handleSelectAllChange"
    />

    <!-- Mensaje de error -->
    <div v-if="showErrorMessage && hasError" :class="errorClass">
      <small>{{ errorMessage }}</small>
    </div>
  </div>
</template>

<style scoped>
.app-multiselect-field:has(.p-multiselect-open) {
  position: relative;
  z-index: 1;
}

:deep(.p-floatlabel:not(:has(.p-multiselect.p-focus)):not(:has(.p-multiselect-open)):not(:has(.p-inputwrapper-focus)):not(:has(.p-invalid)) > label) {
  color: var(--p-surface-400);
}

/* Placeholder */
:deep(.p-multiselect:not(.p-focus):not(.p-multiselect-open):not(.p-invalid) .p-multiselect-label.p-placeholder) {
  color: var(--p-surface-400);
}

:deep(.app-multiselect--chip.p-multiselect) {
  min-height: 2rem;
}

:deep(.p-multiselect-label-container),
:deep(.p-multiselect-label) {
  min-width: 0;
}

:deep(.app-multiselect--chip .p-multiselect-label) {
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  min-height: 1.875rem;
}

:deep(.p-multiselect-label) {
  overflow: hidden;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.25rem;
}

:deep(.p-multiselect-chip) {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  padding: 0.0625rem 0.25rem;
  gap: 0.3rem;
  line-height: 1;
  min-height: 1rem;
}

:deep(.p-multiselect-chip .p-chip-label) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
}

:deep(.p-multiselect-chip .p-chip-remove-icon) {
  flex-shrink: 0;
  width: 0.8rem;
  height: 0.8rem;
  font-size: 0.625rem;
}

html.dark .app-multiselect-field :deep(.p-multiselect) {
  background: color-mix(in srgb, var(--p-surface-900) 18%, transparent) !important;
  color: var(--p-form-field-color);
}

/* El campo no debe limitar el backdrop del panel con append-to="self". */
html.dark .app-multiselect-field :deep(.p-multiselect:not(.p-multiselect-open)) {
  backdrop-filter: blur(8px) saturate(125%);
  -webkit-backdrop-filter: blur(8px) saturate(125%);
}

html.dark .app-multiselect-field :deep(.p-multiselect:not(.p-disabled):not(.p-invalid):is(.p-focus, .p-multiselect-open)) {
  border-color: var(--p-primary-color) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--p-primary-color) 18%, transparent) !important;
}

html.dark .app-multiselect-field :deep(.p-multiselect.p-invalid) {
  border-color: var(--p-multiselect-invalid-border-color) !important;
}

:global(.dark) :deep(.p-floatlabel:not(:has(.p-multiselect.p-focus)):not(:has(.p-multiselect-open)):not(:has(.p-inputwrapper-focus)):not(:has(.p-invalid)) > label) {
  color: var(--p-surface-300);
}

html.dark .app-multiselect-field :deep(.p-multiselect-label.p-placeholder) {
  color: var(--p-form-field-placeholder-color);
}

html.dark .app-multiselect-field :deep(.p-multiselect-chip) {
  background: color-mix(in srgb, var(--p-primary-color) 18%, transparent);
  border: 1px solid color-mix(in srgb, var(--p-primary-color) 25%, transparent);
  color: var(--p-primary-50);
}

:global(.p-multiselect-overlay.app-card-glass) {
  background: color-mix(in srgb, var(--p-surface-0) 95%, transparent) !important;
  border-color: color-mix(in srgb, var(--p-surface-0) 50%, transparent);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

:global(html.dark .app-multiselect-glass-panel) {
  background: color-mix(in srgb, var(--p-primary-950) 42%, transparent) !important;
  color: var(--p-text-color);
  border-color: color-mix(in srgb, var(--p-primary-color) 25%, transparent);
  box-shadow: 0 12px 32px rgb(0 0 0 / 28%);
  --p-checkbox-background: transparent;
  --p-checkbox-border-color: color-mix(in srgb, var(--p-primary-color) 45%, transparent);
}

:global(html.dark .app-multiselect-glass-panel .p-multiselect-filter) {
  background: transparent !important;
  border-color: color-mix(in srgb, var(--p-primary-color) 30%, transparent);
  color: var(--p-form-field-color);
}

:global(html.dark .app-multiselect-glass-panel .p-multiselect-filter:enabled:focus) {
  border-color: var(--p-primary-color);
}

:global(html.dark .app-multiselect-glass-panel .p-multiselect-option-group) {
  background: transparent;
  color: var(--p-text-muted-color);
}

:global(.p-multiselect-overlay.app-card-glass .p-multiselect-header),
:global(.p-multiselect-overlay.app-card-glass .p-multiselect-list-container),
:global(.p-multiselect-overlay.app-card-glass .p-multiselect-list),
:global(.p-multiselect-overlay.app-card-glass .p-multiselect-filter-container) {
  background: transparent !important;
}

:global(.p-multiselect-overlay.app-card-glass .p-multiselect-option) {
  background: transparent !important;
}

:global(.p-multiselect-overlay.app-card-glass .p-multiselect-option:not(.p-disabled):hover) {
  background: color-mix(in srgb, var(--p-primary-color) 18%, transparent) !important;
}

:global(.p-multiselect-overlay.app-card-glass .p-multiselect-option.p-focus) {
  background: color-mix(in srgb, var(--p-primary-color) 25%, transparent) !important;
}

:global(html.dark .app-multiselect-glass-panel .p-multiselect-option.p-multiselect-option-selected) {
  background: color-mix(in srgb, var(--p-primary-color) 22%, transparent) !important;
  color: var(--p-primary-50);
}

:global(html.dark .app-multiselect-glass-panel .p-multiselect-option.p-multiselect-option-selected:not(.p-disabled):is(:hover, .p-focus)) {
  background: color-mix(in srgb, var(--p-primary-color) 32%, transparent) !important;
}
</style>
